"use client";

import { useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

import { getActiveLocaleFromPath } from "@repo/locale";
import { useDispatch } from "react-redux";

import { hydrateLocaleFromSession } from "@repo/ui/helpers/SyncLocale";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";

/**
 * Fallback locale hydrator. When the URL path already carries a valid locale
 * (/{country}/{lang}), the path is the source of truth and the locale slice is
 * seeded server-side (PathLocaleSync), so this no-ops. It only hydrates from the
 * tab session in not-yet-migrated apps whose URLs have no locale segment.
 */
export function LocaleHydrator() {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const hasPathLocale = getActiveLocaleFromPath(pathname ?? "") !== null;

  const sessionId = getTabSessionId();

  // Keep these refs before the effect so they are available to the hydrate logic

  const previousLocale = useRef<string | undefined>(undefined);
  const previousCurrency = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (hasPathLocale) return; // path locale wins; seeded server-side
    if (!sessionId) return;

    hydrateLocaleFromSession(dispatch, sessionId, previousLocale.current, previousCurrency.current)
      .then(({ locale, currency }) => {
        previousLocale.current = locale;
        previousCurrency.current = currency;
      })
      .catch((err) => {
        console.error("LocaleHydrator session hydrate error:", err);
      });
  }, [sessionId, dispatch, hasPathLocale]);

  return null;
}
